import { pageTitle } from '@thottbot/shared';
import { Link } from 'react-router';
import { useAddonInfo } from '../api/client';

const INSTALL_PATH = 'World of Warcraft/_classic_beta_/Interface/AddOns/Thottbot/';
const SAVED_PATH = 'World of Warcraft/_classic_beta_/WTF/Account/<ACCOUNT>/SavedVariables/Thottbot.lua';

export function Addon() {
  const info = useAddonInfo();
  return (
    <div className="page" data-testid="addon-page">
      <title>{pageTitle('Thottbot addon')}</title>
      <h1>Thottbot addon</h1>
      <p>
        A small addon for the WoW: Forever beta client that records what you see while you play
        and writes it to a file you upload here. Every session adds spawn points, drop rates,
        vendor and trainer lists and quest links to the database.
      </p>
      <p className="actions">
        {info.data ? (
          <a className="button" href={info.data.downloadUrl} download data-testid="addon-download">
            Download Thottbot {info.data.version}
          </a>
        ) : (
          <span className="muted">Loading the download link...</span>
        )}
        <Link to="/addon/upload">Upload addon data</Link>
        <Link to="/addon/watch">Auto-upload</Link>
      </p>

      <section className="section">
        <h2>What it records</h2>
        <ul>
          <li>Quests you are offered, progress and turn in: text, level, rewards, and which NPC or object gave and took them, with your position.</li>
          <li>NPCs you mouse over, target or talk to: id, name, title, level, classification, type, reaction, and your position.</li>
          <li>Objects you mouse over (chests, herbs, ore, quest objects): id, name and position.</li>
          <li>Loot windows: the source and every item in it. Each corpse counts once, so drop rates are &ldquo;N% of M looted&rdquo;.</li>
          <li>Vendors: every item with its price, currency and limited stock. Trainers: every spell with its cost and required level.</li>
          <li>Items you see: name, quality, item level, bind, class, sell price and tooltip lines.</li>
          <li>Screenshots you take with its camera button: the file name, what it shows (NPC, object or zone), your position and the time.</li>
        </ul>
      </section>

      <section className="section">
        <h2>What it never records</h2>
        <p>
          Your character name, realm, guild, level, chat, friends or account. The file holds only
          the game entities above, your faction (Alliance or Horde), the addon version and the
          client build. Uploads are credited to the BattleTag you log in with here.
        </p>
      </section>

      <section className="section">
        <h2>Install</h2>
        <ol>
          <li>
            Download the zip above (it is served from this site; nothing to sign up for) and unpack it so the folder is <code>{INSTALL_PATH}</code> (it must contain{' '}
            <code>Thottbot.toc</code>).
          </li>
          <li>Start the game. If the AddOns list flags it as out of date, tick &ldquo;Load out of date AddOns&rdquo;.</li>
          <li>
            Forever loads addons and settings from a previous beta&rsquo;s WTF folder; if you played an earlier
            beta, check the AddOns list on the character screen shows Thottbot enabled.
          </li>
        </ol>
      </section>

      <section className="section">
        <h2>In game</h2>
        <p>
          A minimap button shows the status; left click pauses or resumes recording, right click lists the
          commands, drag moves it. Commands: <code>/thottbot status</code>, <code>/thottbot pause</code>,{' '}
          <code>/thottbot resume</code>, <code>/thottbot clear</code> (start a fresh file),{' '}
          <code>/thottbot shot</code> (the camera button),{' '}
          <code>/thottbot probe</code> (which client APIs are available), <code>/thottbot cursor</code> (what the
          client reports for the chest, node or herb under your mouse).
        </p>
        <p>
          The camera button beside the minimap (or a key bound under Key Bindings, Thottbot) screenshots your
          target NPC or object, else what is under the mouse, else the zone, with the interface hidden except in
          combat. Auto-upload then puts it on that page once the game has written the file.
        </p>
      </section>

      <section className="section">
        <h2>Upload</h2>
        <p>
          The game writes the file when you log out or <code>/reload</code>. Then open{' '}
          <Link to="/addon/upload">Upload addon data</Link>, log in, and choose <code>{SAVED_PATH}</code>.
          {info.data && (
            <>
              {' '}
              Files up to {Math.round(info.data.maxBytes / (1024 * 1024))} MB from addon version {info.data.minVersion} or
              newer on a WoW: Forever client build are accepted.
            </>
          )}{' '}
          Re-uploading the same file after more play replaces your earlier upload; nothing is counted twice.
        </p>
        <p>
          Or let the site upload it for you: on Chrome or Edge, <Link to="/addon/watch">Auto-upload</Link> watches your
          WoW folder while the tab stays open and uploads every file the game writes, and the camera button&rsquo;s
          screenshots (only those; no other screenshot is read).
        </p>
      </section>
    </div>
  );
}
